import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Users, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Groups() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [joinCode, setJoinCode] = useState("");
  const [form, setForm] = useState({ name: "", description: "", meeting_time: "" });

  const load = async () => {
    const { data } = await api.get("/groups");
    setGroups(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Give your group a name.");
    await api.post("/groups", form);
    setForm({ name: "", description: "", meeting_time: "" });
    toast.success("Group created.");
    load();
  };

  const join = async () => {
    if (!joinCode.trim()) return;
    try {
      await api.post("/groups/join", { code: joinCode });
      setJoinCode("");
      toast.success("Joined.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not join.");
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="font-serif text-5xl">Groups</h1>
      <p className="text-muted-foreground mt-3">Walk the eleven weeks with others.</p>

      <div className="grid md:grid-cols-2 gap-6 mt-12">
        <section className="border border-border bg-card p-8">
          <div className="text-xs uppercase tracking-widest text-primary mb-3">Lead a group</div>
          <label className="block mb-4">
            <span className="text-xs text-muted-foreground">Group name</span>
            <input value={form.name} data-testid="group-name" onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="notebook-input w-full mt-1" placeholder="e.g. Wednesday men" />
          </label>
          <label className="block mb-4">
            <span className="text-xs text-muted-foreground">Description</span>
            <Textarea value={form.description} data-testid="group-desc" onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3} className="mt-1" placeholder="Where you meet, what to expect." />
          </label>
          <label className="block mb-4">
            <span className="text-xs text-muted-foreground">Meeting time</span>
            <input value={form.meeting_time} data-testid="group-meeting-time"
              onChange={(e) => setForm({ ...form, meeting_time: e.target.value })}
              className="notebook-input w-full mt-1" placeholder="e.g. Wednesdays 7pm at Sam's" />
          </label>
          <Button onClick={create} data-testid="group-create" className="rounded-full w-full">Create group</Button>
          <p className="text-xs text-muted-foreground italic mt-4">
            The moment you create a group, you become a leader for it — no separate account required.
          </p>
        </section>

        <section className="border border-border bg-card p-8">
          <div className="text-xs uppercase tracking-widest text-primary mb-3">Join with a code</div>
          <label className="block mb-4">
            <span className="text-xs text-muted-foreground">Group code</span>
            <input value={joinCode} data-testid="group-code" onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              className="notebook-input w-full mt-1 uppercase tracking-widest" placeholder="ABC123" />
          </label>
          <Button onClick={join} data-testid="group-join" variant="outline" className="rounded-full w-full">Join</Button>
        </section>
      </div>

      <section className="mt-16">
        <h2 className="font-serif text-2xl mb-6 flex items-center gap-4">
          <span>Your groups</span>
          <span className="flex-1 rule-line mb-1"></span>
        </h2>
        {groups.length === 0 ? (
          <p className="text-muted-foreground italic">You aren't part of any group yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {groups.map((g) => (
              <Link key={g.id} to={`/app/groups/${g.id}`} data-testid={`group-card-${g.id}`}
                className="border border-border bg-card p-6 hover:border-primary/40">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Users className="w-3 h-3" /> {g.member_count} members
                  {g.is_leader && <span className="text-primary ml-auto">Leading</span>}
                </div>
                <div className="font-serif text-xl mt-2">{g.name}</div>
                {g.meeting_time && (
                  <div className="text-xs text-primary mt-2 italic">{g.meeting_time}</div>
                )}
                {g.description && <div className="text-sm text-muted-foreground mt-2">{g.description}</div>}
                <div className="mt-4 text-xs font-mono tracking-widest text-muted-foreground">CODE · {g.code}</div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
